/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Types } from 'bridge-commands/types';

export import SystemInfo = Types.Common.Systeminfo.SystemInfo;
export import Network = Types.Common.Systeminfo.Network;
export import Disk = Types.Common.Systeminfo.Disk;
export import Display = Types.Common.Systeminfo.Display;
export import HID = Types.Common.Systeminfo.HID;
export import BrowserInfo = Types.Common.Systeminfo.BrowserInfo;
export import GPU = Types.Common.Systeminfo.GPU;
export import PhysicalDisk = Types.Common.Systeminfo.PhysicalDisk;

// Common Network type strings on all platforms
export const NetworkTypeConstants = {
    Ethernet: 'Ethernet',
    WiFi2_4: 'WiFi 2.4',
    WiFi5_0: 'WiFi 5.0',
    WiFi6_0: 'WiFi 6.0',
    WiFiUnknown: 'WiFi Unknown',
    Mobile: 'Mobile',
    Mobile_2G: 'Mobile 2G',
    Mobile_3G: 'Mobile 3G',
    Mobile_4G: 'Mobile 4G',
    Mobile_5G: 'Mobile 5G',
    Wimax: 'Wimax',
    Bluetooth: 'Bluetooth',
    Other: 'Other',
    Unknown: 'Unknown'
};

export enum SystemInfoKey {
    DeviceId = 'DeviceId',
    ProductVersion = 'ProductVersion',
    ProductName = 'ProductName',
    Region = 'Region',
    CPUName = 'CPUName',
    //eslint-disable-next-line @typescript-eslint/no-shadow
    Disk = 'Disk',
    //eslint-disable-next-line @typescript-eslint/no-shadow
    PhysicalDisk = 'PhysicalDisk',
    //eslint-disable-next-line @typescript-eslint/no-shadow
    Display = 'Display',
    //eslint-disable-next-line @typescript-eslint/no-shadow
    GPU = 'GPU',
    //eslint-disable-next-line @typescript-eslint/no-shadow
    HID = 'HID',
    //eslint-disable-next-line @typescript-eslint/no-shadow
    Network = 'Network',
    OSName = 'OSName',
    OSVersion = 'OSVersion',
    BrowserName = 'BrowserName',
    BrowserVersion = 'BrowserVersion',
    OSBuildNumber = 'OSBuildNumber',
    PhysicalMemoryCapacity = 'PhysicalMemoryCapacity',
    Platform = 'Platform',
    ProcessorArchitecture = 'ProcessorArchitecture',
    SystemName = 'SystemName',
    UserDefaultUILanguage = 'UserDefaultUILanguage',
    VendorName = 'VendorName',
    SubApiName = 'SubApiName',
    ErrorMessage = 'ErrorMessage',
    ApiStatus = 'ApiStatus',
    DxVersion = 'DxVersion',
    DeviceType = 'DeviceType',
    ConfidenceInBrowser = 'ConfidenceInBrowser',
    IsBrowserForged = 'IsBrowserForged',
    IsConnectedByVM = 'IsConnectedByVM',
    Location = 'Location',
    Make = 'Make',
    Model = 'Model',
    CurrentUiMode = 'CurrentUiMode',
    DisplayDataInfo = 'DisplayDataInfo',
    UserSpecifiedDeviceName = 'UserSpecifiedDeviceName',
    ShellVersion = 'ShellVersion'
}

export const StaticSystemInfoKeyList = [
    SystemInfoKey.CPUName,
    SystemInfoKey.OSName,
    SystemInfoKey.OSVersion,
    SystemInfoKey.BrowserName,
    SystemInfoKey.BrowserVersion,
    SystemInfoKey.OSBuildNumber,
    SystemInfoKey.PhysicalMemoryCapacity,
    SystemInfoKey.Platform,
    SystemInfoKey.ProcessorArchitecture,
    SystemInfoKey.SystemName,
    SystemInfoKey.VendorName,
    SystemInfoKey.DeviceType,
    SystemInfoKey.ConfidenceInBrowser,
    SystemInfoKey.IsBrowserForged,
    SystemInfoKey.DeviceId,
    SystemInfoKey.ProductVersion,
    SystemInfoKey.ProductName,
    SystemInfoKey.Make,
    SystemInfoKey.Model,
    SystemInfoKey.UserDefaultUILanguage,
    SystemInfoKey.DxVersion,
    SystemInfoKey.UserSpecifiedDeviceName
];

/**
 * Prefetch key to be used for prefetching static system info,
 * DxVersion is a costly call and will increase the timing of
 * prefetch. Hence excluding it from prefetch.
 */
export const PrefetchSystemInfoKeyList = StaticSystemInfoKeyList.filter(value => value !== SystemInfoKey.DxVersion);

/**
 * ShellVersion is requested only when featureEnablement.preferShellVersion is set.
 * It is omitted here so bulk system-info calls do not ask other platforms for it.
 */
export const AllSystemInfoKeys = Object.values(SystemInfoKey).filter(key => key !== SystemInfoKey.ShellVersion);

export const Arch64 = 'aarch64';
